import { DateTime } from 'luxon';
import { MetadataType, SurveyQuestionType } from './SiteClasses';

export const TIMEZONE = 'America/Mexico_City';

function nowUnix(){
	return DateTime.now().toUnixInteger();
}

function formatUnix(unix: number, format: string='dd/MMM/yyyy'){
	if(unix===null || unix===undefined) return '';
	return DateTime.fromSeconds(unix, { zone: TIMEZONE }).setLocale('es').toFormat(format);
}

function activityStatus(a: { completed: number, date_activity: number }): { text: string, color: 'green' | 'orange' | 'red' }{
	if(a.completed) return { text: 'Completada', color: 'green' };
	if(a.date_activity < nowUnix()) return { text: 'Retrasada', color: 'red' };
	return { text: 'Pendiente', color: 'orange' };
}

function todayInput(){
	return DateTime.now().setZone(TIMEZONE).toFormat('yyyy-MM-dd');
}

function inputToUnix(value: string){
	var d = DateTime.fromFormat(value || '', 'yyyy-MM-dd', { zone: TIMEZONE });
	return d.isValid ? d.toUnixInteger() : null;
}

function nowDatetimeInput(){
	return DateTime.now().setZone(TIMEZONE).toFormat("yyyy-MM-dd'T'HH:mm");
}

function datetimeInputToUnix(value: string){
	var d = DateTime.fromFormat(value || '', "yyyy-MM-dd'T'HH:mm", { zone: TIMEZONE });
	return d.isValid ? d.toUnixInteger() : null;
}

const bindClose = <T>(setVal: (val: T)=>void, val?: T)=>{
	return ()=>{
		setVal((val as any) || null);
	}
}

const bindClick = <T>(setVal: (val: T)=>void, val?: T)=>{
	return ()=>{
		setVal((val as any) ?? null);
	}
}

function joinUrls(...parts: string[]): string{
	return parts.map((p, i)=> i===0 ? p.replace(/\/$/, '') : p.replace(/^\//, '').replace(/\/$/, '')).join('/') || '/';
}

const bindChange = (setVal: (val: any, ...args: any)=>void)=>{
	return (e: { target: { value: string } } | any)=>{
		return setVal(e.target.value);
	}
}

const bindToggle = (val: any, setVal: any)=>{
	return ()=>{
		setVal(!val);
	}
}

const bindFormChange = <T>(val: T, setVal: (val: any, ...args: any)=>void, setDirty?: (val: boolean)=>void)=>{
	return (name: keyof T, semantic: boolean=false, target: boolean=false)=>{
		return (e: { target: { value: string } } | any, data?: { value?: any, checked: boolean } | any)=>{
			if(semantic){
				setVal({ ...val, [name]: typeof data.checked!=='undefined' ? data.checked : data.value });
			}else if(target){
				setVal({ ...val, [name]: e.target.value })
			}else{
				setVal({ ...val, [name]: e });
			}
			if(setDirty) setDirty(true);
		}
	}
}

function addCommas(num: number, fixed: boolean=true, hideZero: boolean=false){
	if (!num && num !== 0) return null;
	if(typeof num==='string'){
		num = parseFloat(num as string);
	}
	var c = num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
	var dec = c.substring(c.length-2, c.length);
	return fixed ? (hideZero && dec == '00' ? c.slice(0, -3) : c) : c.slice(0, -3);
}

function dashify(what: any, num: number=4, delimiter: string='-'){
	what = what.toString();
	var ret = '';
	for(var i=0; i<what.length; i++){
		if(i%num==0 && i!=0)ret+=delimiter;
		ret += what[i];
	}
	return ret;
}

function unix(){
	return Math.floor(new Date().getTime()/1000);
}

function uniqueArray<T>(array: T[]){
	return Array.from<T>(new Set(array));
}

function validatePassword(val: string){
	return (/^(?=.*[0-9])(?=.*[A-Z])(?=.*[a-z])(?=.*[!@#$%^&*\-\.\,\+\*])[a-zA-Z0-9!@#$%^&*\-\.\,\+\*]{8,32}$/).test(val);
}

function partition<T>(a: T[], n: number) : T[][]{
	var array = [...a];
	return array.length ? [array.splice(0, n)].concat(partition(array, n)) : [];
}

function cropString(str: string, len: number, elip: boolean=false){
	if(!str) return '';
	var elip = elip && str.length>len;
	return str.trim().substring(0, len-(elip ? 3 : 0)).trim() + (elip ? '...' : '');
}

function randomRange(min: number, max: number){
	return Math.floor(Math.random() * (max-min+1)+min);
}

function isJWT(token: string){
	return (/(^[A-Za-z0-9-_]*\.[A-Za-z0-9-_]*\.[A-Za-z0-9-_]*$)/gi).test(token);
}

function getStatusColor(status_id: number): 'green' | 'red' | 'orange' | 'yellow' | 'blue' | 'black'{
	switch(status_id){
		case 1: return 'green';
		case 2: return 'red';
		case 3: return 'orange';
		case 4: return 'blue';
		case 5: return 'red';
		case 6: return 'yellow';
		case 7: return 'blue';
		case 8: return 'orange';
		case 9: return 'green';
		default: return 'black';
	}
}

function getMetadataTypeName(type_id: MetadataType): string{
	switch(type_id){
		case MetadataType.INPUT: return 'Texto';
		case MetadataType.TEXTAREA: return 'Texto largo';
		case MetadataType.DROPDOWN: return 'Opciones';
		case MetadataType.MULTIPLE: return 'Opción múltiple';
		case MetadataType.CHECKBOX: return 'Si / No';
		default: return 'Desconocido';
	}
}

function getSurveyTypeName(type_id: SurveyQuestionType): string{
	switch(type_id){
		case SurveyQuestionType.INPUT: return 'Texto';
		case SurveyQuestionType.TEXTAREA: return 'Texto largo';
		case SurveyQuestionType.DROPDOWN: return 'Opciones';
		case SurveyQuestionType.MULTIPLE: return 'Opción múltiple';
		case SurveyQuestionType.YESNO: return 'Si / No';
		case SurveyQuestionType.RATING: return 'Calificación';
		case SurveyQuestionType.DATE: return 'Fecha';
		case SurveyQuestionType.STATIC: return 'Texto informativo';
		default: return 'Desconocido';
	}
}

function surveyTypeHasValues(type_id: SurveyQuestionType): boolean{
	return [SurveyQuestionType.DROPDOWN, SurveyQuestionType.MULTIPLE, SurveyQuestionType.RATING].indexOf(type_id)!==-1;
}

export {
	activityStatus,
	addCommas,
	bindChange,
	bindClick,
	bindClose,
	bindFormChange,
	bindToggle,
	cropString,
	dashify,
	datetimeInputToUnix,
	formatUnix,
	getMetadataTypeName,
	getStatusColor,
	getSurveyTypeName,
	surveyTypeHasValues,
	inputToUnix,
	isJWT,
	joinUrls,
	nowDatetimeInput,
	nowUnix,
	partition,
	randomRange,
	todayInput,
	uniqueArray,
	unix,
	validatePassword,
}